import React, { Dispatch, FC, SetStateAction } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { Typography } from '@material-ui/core';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

import {
	Airing,
	AlternateIdInput,
	AlternateIdNamespaces,
	CreateAiringResult,
	UpdateAiringResult,
	CreateOrUpdateAiringVariables,
	DeleteAiringResult,
	DeleteAiringVariables,
	DeleteAlternateIdResult,
	DeleteAlternateIdVariables,
	PaginationOptions,
	USE_DRM
} from '../../utils/types';
import { convertAiringToAiringInput } from '../../utils/inputTransform';
import Airings from './Airings';
import { createAiring, updateAiring, deleteAiring } from './apiMutations';
import { useAlertContext } from '../../context/alertContext';
import { deleteAlternateId } from '../airings/apiMutations';

dayjs.extend(duration);

export const AiringsEditor: FC<AiringsEditorProps> = (props) => {
	const { airings, defaultChannelName, gameId, gameStartTime, paginationOptions, sport, useDrm, setAutoRefetch } = props;
	const { setAlert } = useAlertContext() || [];
	const [createAiringMethod] = useMutation<CreateAiringResult, CreateOrUpdateAiringVariables>(createAiring);
	const [updateAiringMethod] = useMutation<UpdateAiringResult, CreateOrUpdateAiringVariables>(updateAiring);
	const [deleteAiringMethod] = useMutation<DeleteAiringResult, DeleteAiringVariables>(deleteAiring);
	const [deleteAlternateIdMethod] = useMutation<DeleteAlternateIdResult, DeleteAlternateIdVariables>(deleteAlternateId);

	const handleAiringAdd = async (airingData: Airing): Promise<unknown> => {
		const airingStartDifference = dayjs.duration(dayjs(airingData.startTime ?? '').diff(dayjs(gameStartTime ?? '')));

		if (
			airingData.startTime &&
			dayjs(airingData.startTime).isBefore(dayjs()) &&
			!window.confirm(
				'The Airing start time is in the past and the MAF service may not work. Would you like to proceed?\n\n[Cancel] to abort Airing creation\n[OK] to proceed'
			)
		)
			return Promise.resolve();

		if (
			Math.abs(airingStartDifference.asHours()) > 1 &&
			!window.confirm(
				'The difference between Airing and Game start times is greater than 1 hour. Would you like to proceed?\n\n[Cancel] to abort Airing creation\n[OK] to proceed'
			)
		)
			return Promise.resolve();

		if ([USE_DRM.YES, USE_DRM.BOTH].includes(useDrm)) {
			const mAltId = window
				.prompt(
					useDrm === USE_DRM.YES
						? 'DRM functionality is enabled - please enter a valid Merlin Alternate Id for this Airing.\n\n[Cancel] to abort Airing creation\n[OK] to proceed - without a valid Merlin Alternate Id Airing creation will be aborted'
						: 'Optionally, provide a valid Merlin Alternate Id to activate DRM functionality on this Airing.\n\n[Cancel] to abort Airing creation\n[OK] to proceed - to exclude DRM leave the Merlin Alternate Id empty',
					''
				)
				?.trim();
			if (typeof mAltId === 'undefined') return Promise.resolve();
			if (useDrm === USE_DRM.YES && mAltId?.length === 0) return Promise.resolve();

			if (mAltId?.length > 0) {
				airingData.drm = true;
				airingData.mAltId = mAltId;
			} else {
				airingData.drm = false;
			}
		}

		const airing = convertAiringToAiringInput(airingData);
		const mafStage = airingData.mafStage && airingData.mafStage !== 'DEFAULT' ? airingData.mafStage : undefined;

		// Add a PROTECTED_AIRING alternateId to the airing
		const protectedAltId: AlternateIdInput = {
			id: `${airing.id}#${AlternateIdNamespaces.PROTECTED_AIRING}:true`,
			alternateId: `airing#${airing.id}`,
			namespace: AlternateIdNamespaces.PROTECTED_AIRING
		};
		airing.alternateIds = airing.alternateIds ? [...airing.alternateIds, protectedAltId] : [protectedAltId];

		await createAiringMethod({
			variables: { airing, gameId, mafStage, action: 'MANUAL' }
		}).catch((error) => {
			setAlert(error.toString());
		});

		paginationOptions.handlePageSizeChange(paginationOptions.pageSize);

		return setTimeout(() => {
			setAutoRefetch(true);
		}, 1000);
	};

	const handleAiringDelete = async ({ id, alternateIds }: Airing): Promise<void> => {
		// Remove Airing
		await deleteAiringMethod({
			variables: { id }
		}).catch((error) => {
			setAlert(error.toString());
		});

		// Remove ALL Alternate IDs associated with the Airing
		if (alternateIds?.items.length) {
			try {
				await Promise.all(
					alternateIds?.items.map(({ id }): ReturnType<typeof deleteAlternateIdMethod> =>
						deleteAlternateIdMethod({
							variables: { id }
						})
					)
				);
			} catch (error) {
				setAlert((error as unknown as Error).toString());
			}
		}

		paginationOptions.handlePageSizeChange(paginationOptions.pageSize);
	};

	const handleAiringUpdate = (newData: Airing, oldData?: Airing): Promise<unknown> => {
		const airing = convertAiringToAiringInput(newData);

		const updatedAiring = updateAiringMethod({
			variables: { airing, gameId }
		}).catch((error) => {
			setAlert(error.toString());
		});
		setAlert('Airing updated');

		return updatedAiring.then(() => paginationOptions.handlePageSizeChange(paginationOptions.pageSize));
	};

	if (paginationOptions.loading) return <Typography>Loading...</Typography>;
	if (paginationOptions.error) return <Typography>Error loading data from the server</Typography>;
	return (
		<Airings
			airings={airings}
			defaultChannelName={defaultChannelName}
			gameId={gameId}
			gameStartTime={gameStartTime}
			onRowAdd={handleAiringAdd}
			onRowDelete={handleAiringDelete}
			onRowUpdate={handleAiringUpdate}
			paginationOptions={paginationOptions}
			setAutoRefetch={setAutoRefetch}
			sport={sport}
			useDrm={useDrm}
		/>
	);
};

type AiringsEditorProps = {
	airings: Airing[];
	defaultChannelName: string;
	gameId: string;
	gameStartTime: string | null;
	paginationOptions: PaginationOptions;
	setAutoRefetch: Dispatch<SetStateAction<boolean>>;
	sport: string | null | undefined;
	useDrm: USE_DRM;
};

export default AiringsEditor;
